/**
 * UserAuditHistory — histórico de auditoria para um usuário.
 *
 * Pattern D-B: componente verifica audit-events-read independentemente
 * do contexto pai (UserDetailDrawer). Se capability ausente → placeholder neutro.
 *
 * Consome: GET /v1/audit-events?entityType=user&entityId=:id
 * Backend: AuditController Wave 1 PR #61 (Item 135 RESOLVIDO).
 */

import { useQuery } from '@tanstack/react-query';

import { useCapability } from '../../../hooks/use-capability';
import { engineUrl } from '../../../lib/runtime-config';
import { useAuthStore } from '../../../stores/auth-store';

interface AuditEventItem {
  id: string;
  eventType: string;
  entityType: string;
  entityId: string;
  actorUserId: string | null;
  severity: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

interface AuditListResponse {
  data: AuditEventItem[];
  nextCursor: string | null;
}

function getEngineUrl(): string {
  return engineUrl();
}

async function fetchAuditEvents(entityId: string): Promise<AuditListResponse> {
  const token = useAuthStore.getState().accessToken;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token !== null && token.length > 0) headers.Authorization = `Bearer ${token}`;
  const qs = new URLSearchParams({ entityType: 'user', entityId, limit: '10' });
  const res = await fetch(`${getEngineUrl()}/v1/audit-events?${qs.toString()}`, {
    method: 'GET',
    headers,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status.toString()}`);
  return (await res.json()) as AuditListResponse;
}

const SEVERITY_STYLE: Record<string, string> = {
  info: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  forensic: 'bg-red-100 text-red-700',
};

interface Props {
  userId: string;
}

export function UserAuditHistory({ userId }: Props): JSX.Element {
  // D-B: capability gate próprio — não confia no pai.
  const canReadAudit = useCapability('audit-events-read');

  const { data, isLoading } = useQuery({
    queryKey: ['audit-events', 'user', userId],
    queryFn: () => fetchAuditEvents(userId),
    enabled: canReadAudit,
    staleTime: 60 * 1000,
  });

  if (!canReadAudit) {
    return (
      <div className="rounded border border-border-subtle bg-surface-raised p-4 text-center text-xs text-text-secondary">
        Histórico de auditoria não disponível — requer{' '}
        <code className="font-mono">audit-events-read</code>.
      </div>
    );
  }

  function formatDate(val: string): string {
    return new Date(val).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return (
    <section>
      <h3 className="mb-3 text-sm font-medium text-text-primary">Histórico de auditoria</h3>
      {isLoading && <p className="text-xs text-text-secondary">Carregando...</p>}
      {!isLoading && (data?.data ?? []).length === 0 && (
        <p className="text-xs text-text-secondary">Nenhum evento registrado.</p>
      )}
      <ul className="flex flex-col gap-2">
        {(data?.data ?? []).map((ev) => (
          <li
            key={ev.id}
            className="flex items-start gap-3 rounded-lg border border-border-subtle p-3 text-xs"
          >
            <span
              className={`mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 font-medium ${SEVERITY_STYLE[ev.severity] ?? 'bg-gray-100 text-gray-600'}`}
            >
              {ev.severity}
            </span>
            <div className="flex-1">
              <p className="font-medium text-text-primary">{ev.eventType}</p>
              <p className="text-text-secondary">{formatDate(ev.createdAt)}</p>
              {ev.actorUserId !== null && (
                <p className="font-mono text-text-secondary">por {ev.actorUserId.slice(0, 8)}…</p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
